import { Button, PageHeader, Descriptions, Image, Spin, Card, message } from 'antd'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'

import Error from '../../components/Error'
import { getFixSkuById, getQrCodeByFixSku } from '../../network/FixSku'
import { useAuth } from '../../hooks/useAuth'
import { getCurrency } from '../../network/Stores'
import { getCurrencySymbol } from '../../utils/currencyUtils'
import { createUrlWithFiltersFromCurrentURL } from '../../utils/filterUtils'

const baseUrl = process.env.REACT_APP_API_URL || ''
const qrStorageFolder = '/storage/qr-codes'

const ViewFixSku = ({ history }) => {
  const { id, storeId } = useParams()
  const { user } = useAuth()
  const [qrImageUrl, setQrImageUrl] = useState(null)
  const [qrLoading, setQrLoading] = useState(false)
  const [qrError, setQrError] = useState(null)
  const [currencySymbol, setCurrencySymbol] = useState('')

  const fixSkuDetails = useQuery(
    ['getFixSkuById', { id }],
    getFixSkuById,
    { refetchOnWindowFocus: false }
  )

  useEffect(() => {
    const fetchCurrency = async () => {
      if (!storeId) return
      try {
        const currencyValue = await getCurrency(storeId)
        const symbol = getCurrencySymbol(currencyValue.currency)
        setCurrencySymbol(symbol)
      } catch (e) {}
    }
    fetchCurrency()
  }, [storeId])

  useEffect(() => {
    if (!id) return
    setQrLoading(true)
    setQrError(null)
    getQrCodeByFixSku(id)
      .then((data) => {
        const uri = data?.image?.uri
        setQrImageUrl(uri ? `${baseUrl}${qrStorageFolder}/${uri}` : null)
        setQrError(uri ? null : 'Invalid QR code response')
      })
      .catch(() => {
        setQrError('Failed to load QR code')
        message.error('Failed to load QR code')
      })
      .finally(() => setQrLoading(false))
  }, [id])

  const data = fixSkuDetails.data
  const name = data?.name || {}
  const description = data?.description || {}

  const printQrCode = () => {
    if (!qrImageUrl) return
    const win = window.open('', '_blank')
    const qrCount = 24
    const qrCells = Array(qrCount)
      .fill(null)
      .map(
        () =>
          `<div style="padding:2px;box-sizing:border-box;"><img src="${qrImageUrl}" alt="QR Code" style="width:100%;height:auto;max-width:120px;display:block;" /></div>`
      )
      .join('')
    win.document.write(`
      <html>
        <head>
          <title>QR Code ${data?.sku || ''}</title>
          <style>
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body style="margin:0;padding:10px;box-sizing:border-box;">
          <div style="display:grid;grid-template-columns:repeat(4, 1fr);gap:0;width:100%;height:98vh;box-sizing:border-box;">
            ${qrCells}
          </div>
        </body>
      </html>
    `)
    win.document.close()
    win.onload = () => {
      win.print()
      win.onafterprint = () => win.close()
    }
  }

  if (fixSkuDetails.isError) {
    return (
      <>
        <PageHeader className="site-page-header" title="View fix SKU" onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku'))} />
        <Error retry={() => fixSkuDetails.refetch()} />
      </>
    )
  }

  if (fixSkuDetails.isLoading || !data) {
    return (
      <>
        <PageHeader className="site-page-header" title="View fix SKU" onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku'))} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        className="site-page-header"
        title="View fix SKU"
        onBack={() => history.push(createUrlWithFiltersFromCurrentURL('/fixed-sku'))}
        style={{ backgroundColor: '#fff' }}
        extra={[
          <Button
            key="edit"
            type="primary"
            onClick={() => history.push(createUrlWithFiltersFromCurrentURL(`/fixed-sku/${id}/${storeId}`))}
          >
            Edit
          </Button>,
        ]}
      />
      <div style={{ padding: 24, backgroundColor: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {data.mainPicture?.uri && (
            <Card style={{ marginBottom: 24 }}>
              <Image
                src={`${baseUrl}/storage/fixed-sku-main-picture/${data.mainPicture.uri}`}
                alt={name.en || name.zh || data.sku}
                style={{ maxWidth: 375, maxHeight: 513, objectFit: 'contain' }}
              />
            </Card>
          )}

          <Card title="Details" style={{ marginBottom: 24 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="SKU">{data.sku}</Descriptions.Item>
              {name.en && <Descriptions.Item label="Name (English)">{name.en}</Descriptions.Item>}
              {name.zh && <Descriptions.Item label="Name (中文)">{name.zh}</Descriptions.Item>}
              {data.retailPrice != null && (
                <Descriptions.Item label={`Retail price (${currencySymbol})`}>
                  {data.retailPrice} {currencySymbol}
                </Descriptions.Item>
              )}
              {data.deliveryTime != null && (
                <Descriptions.Item label="Delivery time (days)">{data.deliveryTime}</Descriptions.Item>
              )}
              {description.en && (
                <Descriptions.Item label="Description (English)">{description.en}</Descriptions.Item>
              )}
              {description.zh && (
                <Descriptions.Item label="Description (中文)">{description.zh}</Descriptions.Item>
              )}
              {data.gender && <Descriptions.Item label="Gender">{data.gender}</Descriptions.Item>}
              {data.customizable != null && (
                <Descriptions.Item label="Customizable">{data.customizable ? 'Yes' : 'No'}</Descriptions.Item>
              )}
              {data.comment && <Descriptions.Item label="Comment">{data.comment}</Descriptions.Item>}
            </Descriptions>
          </Card>

          {Array.isArray(data.sizes) && data.sizes.length > 0 && (
            <Card title="Sizes" style={{ marginBottom: 24 }}>
              <Descriptions bordered column={1} size="small">
                {data.sizes.map((size, idx) => (
                  <Descriptions.Item key={idx} label={size?.name?.en || size?.value || `Size ${idx + 1}`}>
                    {size?.name?.zh && `${size.name.zh} / `}{size?.value}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          )}

          <Card
            title={data.sku ? `QR Code – ${data.sku}` : 'QR Code'}
            extra={
              <Button
                type="primary"
                onClick={printQrCode}
                disabled={qrLoading || !!qrError || !qrImageUrl}
              >
                Print
              </Button>
            }
          >
            {qrLoading && (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin size="large" />
              </div>
            )}
            {qrError && (
              <div style={{ textAlign: 'center', padding: 24, color: '#ff4d4f' }}>
                {qrError}
              </div>
            )}
            {!qrLoading && !qrError && qrImageUrl && (
              <div style={{ textAlign: 'center', padding: 16 }}>
                <Image
                  src={qrImageUrl}
                  alt="QR Code"
                  style={{ maxWidth: 280, maxHeight: 280, objectFit: 'contain' }}
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  )
}

export default ViewFixSku
